import type { ResourceListOptions } from '#dn-types/api.ts';
import type { JoinKey, JoinKeyListItem } from '#dn-types/join-key.ts';
import { rewordValidationMessages } from './errors.ts';
import { del, get, getList, patch, post } from './fetch.ts';

export const DEFAULT_JOIN_KEYS_PAGE_SIZE = 25;

function rewordJoinKeyErrors(err: unknown): never {
  rewordValidationMessages(err, (details) => {
    if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
      return 'You already have a join key with this name, try another.';
    }
    if (details.path === 'expiresAt' && details.code === 'ERR_INVALID_VALUE') {
      return 'The expiration date must be in the future.';
    }
    if (details.path === 'networkID' && details.code === 'ERR_INVALID_REFERENCE') {
      return 'This network no longer exists, choose another.';
    }
    if (details.path === 'roleID' && details.code === 'ERR_INVALID_REFERENCE') {
      return 'This role no longer exists, choose another.';
    }
    if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
      if (typeof details.extra?.limit === 'number') {
        return `You have already created the maximum number of join keys for this organization (${details.extra.limit} keys).`;
      }
      return 'You have already created the maximum number of join keys for this organization.';
    }
  });
}

// POST /join-keys
export type AddJoinKeyVariables = {
  /** A DNS label (lowercase letters, digits, hyphens), since it names hosts enrolled with the key */
  name: string;
  networkID: string;
  roleID?: string;
  tags: string[];
  /** Requirement: each entry must exactly match one of the network's own CIDRs.  Omit to let the API pick. */
  autoAssignCIDRs?: string[];
  /** RFC3339 timestamp.  Omit for a key that never expires. */
  expiresAt?: string;
};
export type AddJoinKeyResult = JoinKey;
export function addJoinKey(formData: AddJoinKeyVariables) {
  return post<AddJoinKeyResult>('/v1/join-keys', formData).catch(rewordJoinKeyErrors);
}

// PATCH /join-keys/:id
export type EditJoinKeyVariables = {
  id: string;
  values: {
    name?: string;
    /** An omitted field is left unchanged; null removes the expiry */
    expiresAt?: string | null;
  };
};
export type EditJoinKeyResult = JoinKeyListItem;
export function editJoinKey({ id, values }: EditJoinKeyVariables) {
  return patch<EditJoinKeyResult>(`/v1/join-keys/${encodeURIComponent(id)}`, values).catch(rewordJoinKeyErrors);
}

// GET /join-keys
export type GetJoinKeysResult = JoinKeyListItem[];
export function getJoinKeys({ cursor = '', pageSize = DEFAULT_JOIN_KEYS_PAGE_SIZE, init }: ResourceListOptions = {}) {
  return getList<GetJoinKeysResult>(`/v1/join-keys?cursor=${encodeURIComponent(cursor)}&pageSize=${pageSize}`, init);
}

// GET /join-keys/:id
export type GetJoinKeyResult = JoinKeyListItem;
export function getJoinKey(id: string, init?: RequestInit) {
  return get<GetJoinKeyResult>(`/v1/join-keys/${encodeURIComponent(id)}`, init);
}

// DELETE /join-keys/:id
export type DeleteJoinKeyResult = Record<string, never>;
export function deleteJoinKey(id: string, init?: RequestInit) {
  return del<DeleteJoinKeyResult>(`/v1/join-keys/${encodeURIComponent(id)}`, init);
}
